import filter from '../../filter';
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { buildServerQuery } from '../../sort-utils';

/**
 * Every row the contact list's current filter matches, as ids.
 *
 * The same shape as the properties list's fetchFullFilteredSet: walked a page
 * at a time rather than asked for in one go, because a filter matching a few
 * thousand rows needs more memory server-side than the request is allowed and
 * the failure is a bare 500 with nothing in the log (see
 * docs/PROJECT-CONTEXT.md). Unlike that one it always throws on failure —
 * Find Dups must know it got the whole list, and quietly comparing the fifty
 * rows on screen would look like a finished search over the filter.
 */

/** The most rows one filter hands over. */
export const FULL_SET_LIMIT = 10000;

/** Rows per request. */
const PAGE_SIZE = 500;

/**
 * Query keys that are not filters — paging and sorting.
 */
const NON_FILTER_KEYS = ['page', 'order', 'limit', 'offset'];

/**
 * Whether the user has actually narrowed this list.
 *
 * An unfiltered list is every row in the table, and these tables run to
 * ~110,000 rows: walking all of them into the browser to hand the ids
 * straight back is slow, and the id cap would quietly drop the rest. Those
 * searches go to the server's own whole-table scan instead (`null` ids),
 * which is what the popup did before filtering existed.
 */
export function hasFilter(query) {
    return Object.keys(query || {}).some(
        (key) => NON_FILTER_KEYS.indexOf(key) === -1
            && query[key] !== undefined && query[key] !== null && query[key] !== '',
    );
}

/**
 * @param {string} queryName  the list's Arc query (e.g. INVESTOR_LIST)
 * @param {string} filterKey  the key filter.js knows this list by
 * @param {object} query      the screen's location.query
 * @returns {Promise<number[]|null>}  null means "no filter — scan the table"
 */
export default function fetchFilteredIds(queryName, filterKey, query) {
    if (!hasFilter(query)) return Promise.resolve(null);

    return fetchListIds(queryName, filterKey, query, {
        limit: FULL_SET_LIMIT,
        tooMany: `This filter matches more than ${FULL_SET_LIMIT.toLocaleString()} records. `
            + 'Narrow it before searching for duplicates.',
    });
}

/**
 * Every row the list's current query matches, as ids, filtered or not — for
 * callers that act on exactly the rows on screen (the Vendors and Agents mail
 * merge) rather than falling back to a whole-table scan of their own.
 *
 * Throws `tooMany` once `limit` ids have been collected.
 *
 * @param {{limit: number, tooMany: string}} bounds
 * @returns {Promise<number[]>}
 */
export function fetchListIds(queryName, filterKey, query, bounds) {
    const collected = [];

    const fetchPage = (offset) => {
        const options = filter(buildServerQuery({ ...query }), filterKey);
        options.param.limit = PAGE_SIZE;
        options.param.offset = offset;

        return executeBackendQuery({ queryName, options }).then((result) => {
            if (!result.success) throw new Error('This list could not be loaded.');

            const rows = result.data || [];

            rows.forEach((row) => {
                const id = row && Number(row.ARCIMEDES_ROW_ID);
                // The lists carry deleted rows and filter them client-side.
                if (id && !isDeleted(row)) collected.push(id);
            });

            // Acting on the first 10,000 of a wider filter and reporting it
            // as finished is the failure that looks like success, so it says
            // so instead.
            if (collected.length >= bounds.limit) {
                throw new Error(bounds.tooMany);
            }

            // A short page is the last one.
            if (rows.length < PAGE_SIZE) {
                return collected;
            }

            return fetchPage(offset + PAGE_SIZE);
        });
    };

    return fetchPage(0);
}

function isDeleted(row) {
    const deleted = row.DELETED;

    return deleted === '1' || deleted === 1
        || (deleted != null && deleted !== '' && deleted !== '0');
}
